import { Shape } from 'lincd/shapes/Shape';
import { JSONWriter } from 'lincd-jsonld/utils/JSONWriter';
import { JSONParser } from 'lincd-jsonld/utils/JSONParser';
import { lincd } from 'lincd/ontologies/lincd';
import { npm } from 'lincd/ontologies/npm';
import { CoreMap } from 'lincd/collections/CoreMap';
import { LinkedStorage } from 'lincd/utils/LinkedStorage';
function isNodejs() {
    return (typeof 'process' !== 'undefined' &&
        process &&
        process.versions &&
        process.versions.node);
}
export class LincdServerProxy extends Shape {
    /**
     * Default headers to be sent with every request
     *
     * @param headers
     */
    static addDefaultHeaders(headers) {
        this.defaultHeaders = Object.assign(this.defaultHeaders, headers);
    }
    static registerActionHandler(actionName, handler) {
        if (!this.actionHandlers.has(actionName)) {
            this.actionHandlers.set(actionName, []);
        }
        this.actionHandlers.get(actionName).push(handler);
    }
    /**
     * Create a response action object that can be returned by a server call to trigger an action on the frontend.
     * @param actionName
     * @param args
     */
    static createResponseAction(actionName, ...args) {
        const key = LincdServerProxy.RESPONSE_ACTION_KEY;
        //@ts-ignore
        return { [key]: actionName, args };
    }
    async call(shapeOrPackageName, method, ...args) {
        if (typeof shapeOrPackageName === 'string') {
            return this.callBackendMethod(shapeOrPackageName, method, args);
        }
        else {
            return this.callShapeMethod(shapeOrPackageName, method, args);
        }
    }
    callCustomShapeMethod(shape, method, methodName, body, headers) {
        let { shapeClass, SHACL_Shape, packageName, shapeURI } = this.parseShape(shape);
        //NOTE: custom calls are not going straight to the localServer on nodejs, so that request.body is available.
        return fetch(`${this.namedNode.uri}/call/${packageName}/${shapeClass.name}/${methodName}?shapeURI=${SHACL_Shape.namedNode.uri}`, {
            method: method,
            headers: headers || {}, //NOTE: custom shape methods do not use LincdServerProxy.defaultHeaders,
            body,
        })
            .then((res) => {
            if (res.ok) {
                return res.json();
            }
            else {
                console.warn('Could not complete server call: ' + res.statusText);
                throw new Error(`Could not complete server call: ${res.statusText}`);
            }
        })
            .then((json) => {
            if (json.__action) {
                this.handleResponseAction(json.__action);
            }
            return JSONParser.parseObject(json).then(({ content }) => {
                return content;
            });
        })
            .catch((err) => {
            console.warn('Error during server call: ', err);
            throw err;
        });
    }
    async customPost(route, ...args) {
        //send data but not graphs (because frontend will have different graphs)
        let body = await JSONWriter.stringify(args, true, false);
        return this.fetchBackend(`${this.namedNode.uri}${route}`, body);
    }
    async callBackendMethod(packageName, methodOrConfig, args) {
        let [method, headers, setLoaded, overwriteData] = this.parseMethod(methodOrConfig);
        //if this IS the backend
        if (this.localServer &&
            (typeof methodOrConfig == 'string' || !methodOrConfig.forceFetch)) {
            //then call the server directly
            return this.localServer.callBackendMethod(packageName, method, args);
        }
        else {
            //send data but not graphs (because frontend will have different graphs)
            let body = await JSONWriter.stringify({ args }, true, false);
            let root = this.namedNode.uri;
            //in multicore development, we need to access the site from 127.0.0.1, and so all requests need to go there
            // // if (process.env.NODE_ENV === 'development' && process.env.NUM_WORKER_PROCESSES) {
            // //   root = root.replace('localhost', '127.0.0.1');
            // // }
            return this.fetchBackend(`${root}/call/${packageName}/${method}`, body, headers, setLoaded, overwriteData);
        }
    }
    parseMethod(method) {
        if (typeof method === 'string') {
            return [method];
        }
        else {
            return [
                method.method,
                method.headers,
                method.setLoaded,
                method.overwriteData,
            ];
        }
    }
    async callShapeMethod(shape, methodOrConfig, args) {
        let [method, headers, setLoaded, overwriteData] = this.parseMethod(methodOrConfig);
        let { shapeClass, shapeURI, packageName } = this.parseShape(shape);
        if (this.localServer &&
            (typeof methodOrConfig == 'string' || !methodOrConfig.forceFetch)) {
            return this.localServer.callShapeMethod(packageName, method, shapeURI, shape instanceof Shape ? shape.node : null, args);
        }
        //SHACL Shapes are generated by using @linkedShape.
        //They are given a unique but deterministic temporary URI
        //We send that URI over to the server, which will have generated the same URI and thus recognise the shape
        //other params: send data but not graphs (because frontend will have different graphs)
        let body = await JSONWriter.stringify({
            shapeURI,
            instanceNode: shape instanceof Shape ? shape.node : null,
            args,
        }, true, false);
        let root = this.namedNode.uri;
        //in multicore development, we need to access the site from 127.0.0.1, and so all requests need to go there
        // // if (process.env.NODE_ENV === 'development' && process.env.NUM_WORKER_PROCESSES) {
        // //   root = root.replace('localhost', '127.0.0.1');
        // // }
        return this.fetchBackend(`${root}/call/${packageName}/${shapeClass.name}/${method}`, body, headers, setLoaded, overwriteData);
    }
    parseShape(shape) {
        let shapeClass;
        if (shape instanceof Shape) {
            shapeClass = Object.getPrototypeOf(shape).constructor;
        }
        else {
            shapeClass = shape;
        }
        let SHACL_Shape = shapeClass.shape;
        let shapeURI = SHACL_Shape.namedNode.uri;
        //get the shape class that defines this shape
        let shapeClassNode = SHACL_Shape.getOneInverse(lincd.definesShape);
        //get the module that exports this shape class
        let packageNode = shapeClassNode.getOne(lincd.module);
        let packageName = packageNode.getValue(npm.packageName);
        return {
            shapeClass,
            SHACL_Shape,
            shapeURI,
            shapeClassNode,
            packageNode,
            packageName,
        };
    }
    async fetchBackend(url, body, headers, setLoaded = false, overwriteData = false) {
        // console.log(`${process.pid} - fetching ${url}`);
        return fetch(url, {
            method: 'POST',
            headers: Object.assign(LincdServerProxy.defaultHeaders, headers || {}),
            body,
        })
            .then((res) => {
            if (res.ok) {
                // console.log(url, 'response: ' + res.status + ' ' + res.statusText);
                return res.json().catch((err) => {
                    console.warn('Could not parse JSON from response: ', err);
                    res
                        .text()
                        .then((text) => {
                        console.warn('Response text: ', text);
                    })
                        .catch((err) => {
                        console.warn('Also could not parse text from response. ', err);
                    });
                });
            }
            else {
                console.warn('Could not complete server call: ' + res.statusText);
            }
        })
            .then((json) => {
            //if instead of a normal data response the server returns an action, handle it
            if (json && json[LincdServerProxy.RESPONSE_ACTION_KEY]) {
                if (this.handleResponseAction(json[LincdServerProxy.RESPONSE_ACTION_KEY])) {
                    return;
                }
            }
            // console.log(url, 'converting response to native objects: ' + json);
            //added defaultStorageGraph, because before that data coming in from backend calls was added to the default graph
            //but then changing any properties of the nodes in this data would cause graph changes in LincdStorage.assignQuadsToGraph -> moveAllQuadsOfNodeIfRequired
            //NOTE: only as long as there is one main storage graph. If there are multiple, this will need to be updated
            //Ideally all quads parsed here already have a graph, so that this is not necessary
            return JSONParser.parseObject(json, LinkedStorage.getDefaultStorageGraph(), setLoaded, null, overwriteData).then(({ content }) => {
                // console.log(url, 'response: ' + content.toString());
                return content;
            });
        })
            .catch((err) => {
            console.warn('Error during server call: ', err);
            throw err;
        });
    }
    handleResponseAction(action) {
        let preventdefault = false;
        if (LincdServerProxy.actionHandlers.has(action)) {
            LincdServerProxy.actionHandlers.get(action).forEach((handler) => {
                handler({ preventDefault: () => (preventdefault = true) });
            });
        }
        return preventdefault;
    }
}
/**
 * Use this key in a json response to trigger an action on the frontend.
 */
LincdServerProxy.RESPONSE_ACTION_KEY = '__action';
LincdServerProxy.actionHandlers = new CoreMap();
LincdServerProxy.defaultHeaders = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
};
//# sourceMappingURL=LincdServerProxy.js.map